import {
  GroupedSelectOptionType,
  SelectOptionType,
} from '@/app/types/constantDataType'
import { useState } from 'react'
import Select from 'react-select'
import { colourStyles } from './selectStyle'

/**
 * Props for the SingleSelect component with grouped options.
 */
interface SingleSelectType {
  id: string
  placeholder: string
  value: SelectOptionType | null
  options: GroupedSelectOptionType[]
  setValue: (value: SelectOptionType | null) => void
  isLoading?: boolean
  isDisabled?: boolean
  inputValue?: string
  setInputValue?: (value: string) => void
  components?: any
  error?: boolean
  noOptionsMessage?: string
}

/**
 * Grouped SingleSelect Component
 *
 * A customizable, single-select dropdown component with support for grouped options.
 *
 * @component
 * @example
 * const groupedOptions = [
 *   {
 *     label: 'Group 1',
 *     options: [
 *       { id: 1, value: 'Option1', label: 'Option 1' },
 *       { id: 2, value: 'Option2', label: 'Option 2' },
 *     ]
 *   },
 *   {
 *     label: 'Group 2',
 *     options: [
 *       { id: 3, value: 'Option3', label: 'Option 3' },
 *       { id: 4, value: 'Option4', label: 'Option 4' },
 *     ]
 *   }
 * ];
 *
 * const [selectedOption, setSelectedOption] = useState<SelectOptionType | null>(null);
 *
 * <GroupedSingleSelect
 *   id="grouped-single-select"
 *   placeholder="Select an option"
 *   value={selectedOption}
 *   options={groupedOptions}
 *   setValue={setSelectedOption}
 * />
 */
const GroupedSingleSelect = ({
  id,
  placeholder,
  value,
  options,
  setValue,
  isLoading = false,
  isDisabled = false,
  components,
  inputValue,
  setInputValue,
  noOptionsMessage,
}: SingleSelectType) => {
  const [localSearchText, setLocalSearchText] = useState<string>('')

  const handleInputChange = (e: string) => {
    if (setInputValue) {
      setInputValue(e)
    } else {
      setLocalSearchText(e)
    }
  }

  return (
    <Select
      id={id}
      inputValue={inputValue || localSearchText}
      onInputChange={handleInputChange}
      isLoading={isLoading}
      isDisabled={isDisabled}
      placeholder={placeholder}
      value={value?.id == -1 ? null : value}
      components={components}
      options={options}
      noOptionsMessage={() => noOptionsMessage || 'No Options'}
      styles={colourStyles}
      onChange={(selectedOption) =>
        setValue(selectedOption as unknown as SelectOptionType | null)
      }
      closeMenuOnSelect={true}
    />
  )
}

export default GroupedSingleSelect
